import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Search, X, Layers, Check } from "lucide-react";

export default function TermsMobileNav({
  sections = [],
  activeSection,
  onSelectSection = () => {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");

  const filteredSections = useMemo(() => {
    const q = (mobileSearch || "").trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(
      (sec) =>
        sec.tabTitle.toLowerCase().includes(q) ||
        sec.shortTitle?.toLowerCase().includes(q)
    );
  }, [sections, mobileSearch]);

  const handleSelect = (id) => {
    onSelectSection(id);
    setIsOpen(false);
    setMobileSearch("");
  };

  const currentIndex =
    sections.findIndex((s) => s.id === activeSection?.id) + 1;
  const formattedNum = String(currentIndex || 1).padStart(2, "0");

  return (
    <div className="terms-mobile-bar d-lg-none py-2 px-3">
      {/* Active Section Trigger Button */}
      <button
        type="button"
        className="terms-mobile-btn mb-2"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls="terms-mobile-collapse"
      >
        <div className="d-flex align-items-center gap-2 overflow-hidden text-start">
          <span className="badge bg-dark text-white">{formattedNum}</span>
          <span className="text-truncate">
            {activeSection?.tabTitle || "Select Section"}
          </span>
        </div>
        <div className="d-flex align-items-center gap-1 text-muted ps-2 flex-shrink-0">
          <small className="fw-normal">Change</small>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Swipeable Horizontal Quick Pills */}
      <div className="terms-mobile-pills" role="tablist">
        {sections.map((sec, idx) => {
          const isActive = sec.id === activeSection?.id;
          return (
            <button
              key={sec.id}
              type="button"
              className={`terms-pill ${isActive ? "terms-pill--active" : ""}`}
              onClick={() => onSelectSection(sec.id)}
            >
              <span className="me-1 opacity-75">{idx + 1}.</span>
              {sec.shortTitle || sec.tabTitle}
            </button>
          );
        })}
      </div>

      {/* Dropdown Drawer for Mobile */}
      {isOpen && (
        <div
          id="terms-mobile-collapse"
          className="bg-white border rounded-3 shadow-lg p-3 mt-2"
          style={{ maxHeight: "70vh", display: "flex", flexDirection: "column" }}
        >
          <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <Layers size={17} className="text-primary" />
              <strong className="small text-dark">Select Terms Section</strong>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border-0 p-1"
              onClick={() => setIsOpen(false)}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          <div className="position-relative mb-2">
            <Search
              size={14}
              className="position-absolute start-0 top-50 translate-middle-y ms-2 text-muted"
            />
            <input
              type="text"
              className="form-control form-control-sm ps-4"
              placeholder="Search sections..."
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div
            className="overflow-y-auto"
            style={{ maxHeight: "calc(70vh - 120px)" }}
          >
            {filteredSections.map((sec) => {
              const isActive = sec.id === activeSection?.id;
              const idx = sections.findIndex((s) => s.id === sec.id) + 1;
              return (
                <button
                  key={sec.id}
                  type="button"
                  className={`btn btn-sm w-100 text-start d-flex align-items-center justify-content-between py-2 px-2 mb-1 rounded ${
                    isActive
                      ? "btn-primary text-white fw-semibold"
                      : "btn-light text-dark"
                  }`}
                  onClick={() => handleSelect(sec.id)}
                >
                  <div className="d-flex align-items-center gap-2 text-truncate me-2">
                    <span
                      className={`badge ${
                        isActive
                          ? "bg-white text-primary"
                          : "bg-secondary text-white"
                      }`}
                      style={{ fontSize: "0.72rem" }}
                    >
                      {idx}
                    </span>
                    <span className="text-truncate">{sec.tabTitle}</span>
                  </div>
                  {isActive && <Check size={16} className="flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

